import { useEffect, useMemo, useState } from "react";
import { Cell } from "react-table";
import { getBuildingAsync } from "src/api/building/building-api";
import { BuildingModel } from "src/api/building/building-types";
import { useApiOperation } from "src/api/hooks";
import { getBuildingDeviceAsync } from "src/api/iot/iot-api";
import { DeviceModel, IoTDeviceRegistration } from "src/api/iot/iot-types";
import { BaseTooltip } from "src/components";
import GoToListButton from "src/components/GoToListButton";
import InnerTable from "src/components/InnerTable";
import { useModal } from "src/recoil/modalState/hook";
import IotConnectedListModal from "./IotConnectedListModal";

type Props = {
  detail?: IoTDeviceRegistration;
};

const IotConnectedInfo = ({ detail }: Props) => {
  const [devicses, setDevicses] = useState<DeviceModel[]>([]);
  const [buildings, setBuildings] = useState<BuildingModel[]>([]);
  const { executeAsync: getBuildingDevice } = useApiOperation(getBuildingDeviceAsync);
  const { executeAsync: getBuilding } = useApiOperation(getBuildingAsync);
  const { setAbstractModalZ1 } = useModal();

  // 건물 연결 id 는 파트너가 이용할 일이 없는 값이라 노출하지 않는다.
  const columns = [
    {
      Header: "건물명",
      accessor: "building.buildingName",
      width: 120,
      Cell: ({ value }: Cell<DeviceModel>) => {
        return <BaseTooltip contents={value} className="text-left w-100" />;
      },
    },
    {
      Header: "위치유형",
      accessor: "building.location.category",
      width: 80,
      Cell: ({ value }: Cell<DeviceModel>) => {
        let location = value === "FLOOR" ? "층" : value === "ROOM" ? "호실" : "공용공간";

        return <span>{location}</span>;
      },
    },
    {
      Header: "층",
      accessor: "building.floor",
      width: 100,
      Cell: ({ row }: Cell<any>) => {
        const notGround = String(row.original.building.floor).includes("-");
        const value = String(row.original.building.floor).replace("-", "");
        return <span className="text-left w-100">{!notGround ? ` 지상 ${value}층` : `지하 ${value}층`}</span>;
      },
    },
    {
      Header: "호",
      accessor: "building.ho",
      width: 100,
    },
    {
      Header: "공용공간 유형",
      accessor: "building.facility",
      width: 140,
      Cell: ({ value, row }: Cell<DeviceModel>) => {
        return <span>{value === "DESK" ? "좌석" : value === "MEETING_ROOM" ? "회의실" : value === "REFRESH_ROOM" ? "편의시설" : "-"}</span>;
      },
    },
    {
      Header: "공용공간",
      accessor: "building.location.facilityName",
      width: 160,
      Cell: ({ value, row }: Cell<DeviceModel>) => {
        const facilityName = row.original.building.facility === "DESK" ? `${row.original.building.location.deskGroupName} > ${value}` : value;
        return <BaseTooltip contents={facilityName} className="text-left w-100" />;
      },
    },
    {
      Header: "상품",
      accessor: "product",
      width: 100,
      Cell: ({ value, row }: Cell<DeviceModel>) => {
        return (
          <span
            className="text-underline text-blue cursor-pointer"
            onClick={() => {
              setAbstractModalZ1({
                isOpen: true,
                children: <IotConnectedListModal iotData={row.original} type="product" onClose={() => setAbstractModalZ1({ isOpen: false })} />,
              });
            }}
          >
            보기
          </span>
        );
      },
    },
    {
      Header: "신청/계약",
      accessor: "contract",
      width: 100,
      Cell: ({ value, row }: Cell<DeviceModel>) => {
        return (
          <span
            className="text-underline text-blue cursor-pointer"
            onClick={() => {
              setAbstractModalZ1({
                isOpen: true,
                children: <IotConnectedListModal iotData={row.original} type="contract" onClose={() => setAbstractModalZ1({ isOpen: false })} />,
              });
            }}
          >
            보기
          </span>
        );
      },
    },
  ];

  useEffect(() => {
    const fetchBuildingIotDevices = async () => {
      if (detail) {
        const { data } = await getBuildingDevice({ registeredDeviceId: detail.id });
        setDevicses(data.data.content);
        await fetchAllBuildingDetails(data.data.content);
      }
    };
    fetchBuildingIotDevices();
  }, [detail]);

  // 건물 상세 호출
  const fetchBuildingDetail = async (id: number) => {
    if (id) {
      return await getBuilding({ buildingId: id })
        .then(({ data }) => data.data.content.building)
        .catch(() => undefined);
    }
  };
  // 건물 상세 api
  const fetchAllBuildingDetails = async (deviceList: DeviceModel[]) => {
    const buildingIds = String(deviceList.map((device) => device.building.id)).split(",");
    const notDuplicatesIds = [...new Set(buildingIds)]; // 건물 id 중복제거

    if (buildingIds.length > 0) {
      //
      //  건물 id 갯수만큼 상세 호출
      const buildingsPromise = notDuplicatesIds.map((id) => fetchBuildingDetail(Number(id)));
      try {
        const allBuildings = await Promise.all(buildingsPromise);
        setBuildings(allBuildings as BuildingModel[]);
      } catch (error) {
        console.log(error);
      }
    }
  };

  const iotDevices = useMemo(() => {
    let _iotDevices: any = [];
    if (devicses.length > 0 && buildings.length > 0) {
      // 건물 층 정보
      const buildingFloor = buildings?.flatMap((buildnig) => buildnig?.buildingFloorList);

      // 건물 층/호 정보
      const buildingHo = buildingFloor?.flatMap((floor) =>
        floor?.buildingRoomList?.map((room: any) => ({ floorNum: floor.floorNum, floorName: floor.floorName, ...room })),
      );

      // 건물 공용공간 정보
      const buildingFacility = buildings?.flatMap((building) => building?.buildingCommonFacility);
      const meetingRooms = buildingFacility.flatMap((facility) => facility?.meetingRoomList) || [];
      const refreshRooms = buildingFacility.flatMap((facility) => facility?.refreshRoomList) || [];
      const deskList =
        buildingFacility
          .flatMap((facility) => facility?.deskSpace?.deskGroupList)
          ?.filter((group) => group?.deskList && group.deskList.length > 0)
          ?.flatMap((group) => group?.deskList?.map((desk) => ({ ...desk, groupName: group.groupName }))) || [];
      const facilityMerged = [...meetingRooms, ...refreshRooms, ...deskList];

      _iotDevices = devicses.map((device) => {
        const floorNum = buildingFloor?.find(
          (floor) => device.building.location.category === "FLOOR" && String(floor?.id) === String(device.building.location.identifier),
        )?.floorNum;

        const floorHoNum = buildingHo?.find(
          (room) => device.building.location.category === "ROOM" && String(room?.id) === String(device.building.location.identifier),
        )?.floorNum;

        const facility = facilityMerged?.find((room) => String(room?.id) === String(device.building.location.identifier));

        // 공용공간 명칭
        const facilityName = facility?.facilityName;
        // 좌석 그룹명 - DESK 면 좌석그룹명 까지 노출
        const deskGroupName = facility?.groupName;

        let data = {
          id: device.id,
          building: {
            ...device.building,
            buildingName: buildings.find((building) => building?.id === device.building?.id)?.buildingName,
            floor:
              floorNum || //
              floorHoNum ||
              `${!facility?.isGround ? "-" : ""}${facility?.floorNum}` ||
              "-",
            ho:
              (device.building.location.category === "ROOM" &&
                buildingHo?.find((room) => String(room?.id) === String(device.building.location.identifier))?.roomNum) ||
              "-",
            facility: facility?.commonFacilityType || "-",
            location: {
              ...device.building.location, //
              // 핫픽스 추가 facilityName
              facilityName: facilityName || "-",
              deskGroupName: deskGroupName || "",
            },
          },
          internalDisplayName: device.internalDisplayName,
          registeredDeviceId: device.registeredDeviceId,
          labels: device.labels,
          platformDevice: {
            ...device.platformDevice,
            platformAccount: {
              ...device.platformDevice.platformAccount,
              platform:
                device.platformDevice.platformAccount.platform === "ST"
                  ? "SmartThings"
                  : device.platformDevice.platformAccount.platform === "B_IOT"
                  ? "b.IoT"
                  : "ST Ent.",
            },
          },
        };

        return data;
      });
    }

    return _iotDevices;
  }, [buildings, devicses]);

  return (
    <>
      <div className="contents-container__scroll">
        <div className="contents-container__wrap">
          <article className="contents-container__wrap-article">
            <div className="contents-container__sub-title">
              <h2>연결정보</h2>
            </div>
            <InnerTable data={iotDevices || []} columns={columns} />
          </article>
        </div>
      </div>
      <div className="contents-container__btn-wrap">
        <div className="left-area">{<GoToListButton />}</div>
        <div className="right-area"></div>
      </div>
    </>
  );
};

export default IotConnectedInfo;
